import * as React from "react";
import {
  Box,
  Typography,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  TextField,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import moment from "moment";


const fmt = (d) => (d ? moment(d).format("MM/DD/YYYY") : "—");

const expectedRows = [
  { key: "mentalhealthsymptoms", label: "Mental Health\nSymptoms" },
  { key: "mentalhealthserviceneeds", label: "Mental Health\nService Needs" },
  { key: "drugsalcohol", label: "Drugs/Alcohol" },
  { key: "vocational", label: "Vocational" },
  { key: "educational", label: "Educational" },
  { key: "social", label: "Social" },
  { key: "interpersonal", label: "Interpersonal" },
  { key: "selfcare", label: "Self-Care" },
  { key: "medical", label: "Medical" },
  { key: "dental", label: "Dental" },
  { key: "financial", label: "Financial" },
  { key: "housing", label: "Housing" },
  { key: "transportation", label: "Transportation" },
  { key: "other", label: "Other" },
];

const staffRoles = [
  { key: "staffMHProfessional", label: "MH Professional" },
  { key: "staffMHPractitioner", label: "MH Practitioner" },
  { key: "staffRehabWorker", label: "Rehab Worker" },
  { key: "staffRN", label: "RN" },
];

const supervisorRoles = [
  { key: "supervisorMHProfessional", label: "MH Professional" },
  { key: "supervisorMHPractitioner", label: "MH Practitioner" },
  { key: "supervisorRehabWorker", label: "Rehab Worker" },
  { key: "supervisorRN", label: "RN" },
];

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "https://neuroez4.devforhealth.com/api";

async function fetchJSONSafe(url, options = {}) {
  const res = await fetch(url, {
    headers: { Accept: "application/json", ...(options.headers || {}) },
    ...options,
  });
  let data = {};
  try {
    const text = await res.text();
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }
  return { ok: res.ok, status: res.status, data };
}

const pickText = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

const ARMHSFunctionalAssessmentPreview = ({ patientFormId: propPatientFormId, id, data = {} }) => {
  const patientFormId = propPatientFormId || id || data?.patientFormId || data?.id;

  const [form, setForm] = React.useState({});
  const [staff, setStaff] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  // Fixed label column width for aligned inline fields
  const LABEL_COL_WIDTH = 340;

  React.useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      setForm({});
      setStaff({});

      try {
        const token = localStorage.getItem('neuro_patientportal_auth_token')
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [formRes, staffRes] = await Promise.all([
          fetchJSONSafe(`${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`, { headers }),
          fetchJSONSafe(`${API_BASE_URL}/Rehabilitative/getStaffassesmentById?patientFormId=${patientFormId}`, { headers }),
        ]);

        if (!active) return;

        if (!formRes.ok) throw new Error(`Forms request failed (${formRes.status})`);
        if (!staffRes.ok) throw new Error(`Staff assessment request failed (${staffRes.status})`);

        const formData = Array.isArray(formRes.data) ? (formRes.data[0] || {}) : (formRes.data || {});
        const staffData = Array.isArray(staffRes.data) ? (staffRes.data[0] || {}) : (staffRes.data || {});

        setForm(formData);
        setStaff(staffData);
      } catch (e) {
        if (active) setError(e.message || "Failed to load data");
      } finally {
        if (active) setLoading(false);
      }
    };

    if (patientFormId) load();
    return () => {
      active = false;
    };
  }, [patientFormId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  // Header + patient info
  const title = "ARMHS Functional Assessment";
  const dos = staff.assessmentDate || form.assessmentDate || null;
  const fileVal = form.accountNo ?? "—";
  const nameVal = form.patientName || "—";
  const dobVal = form.dob || staff.patientDOB || null;
  const clientID = form.patientId || staff.patientID || "—";

  // Time of Functional Assessment + dates
  const timeFA = staff.initial
    ? "Initial"
    : staff.discharge
      ? "Discharge"
      : staff.ongoing
        ? "Ongoing"
        : "—";

  const faDate = staff.functionalDate || staff.assessmentDate || form.assessmentDate || null;
  const diagnosticDate = staff.diagnosticDate || null;

  // Build rows (Status + Functioning with fallbacks)
  const statusByKey = {
    mentalhealthsymptoms: staff.symptomsStatus,
    mentalhealthserviceneeds: staff.servicesStatus,
    drugsalcohol: staff.drugsalcoholStatus,
    vocational: staff.vocationalStatus,
    educational: staff.educationalStatus,
    social: staff.socialStatus,
    interpersonal: staff.interpersonalStatus,
    selfcare: staff.selfcareStatus,
    medical: staff.medicalStatus,
    dental: staff.dentalStatus,
    financial: staff.financialStatus,
    housing: staff.housingStatus,
    transportation: staff.transportationStatus,
    other: staff.otherStatus,
  };

  const funcByKey = {
    mentalhealthsymptoms: staff.symptomsFunctioning,
    mentalhealthserviceneeds: staff.servicesFunctioning,
    drugsalcohol: staff.drugsalcoholFunctioning,
    vocational: staff.vocationalFunctioning,
    educational: staff.educationalFunctioning,
    social: staff.socialFunctioning,
    interpersonal: staff.interpersonalFunctioning,
    selfcare: staff.selfcareFunctioning,
    medical: staff.medicalFunctioning,
    dental: staff.dentalFunctioning,
    financial: staff.financialFunctioning,
    housing: staff.housingFunctioning,
    transportation: staff.transportationFunctioning,
    other: staff.otherFunctioning,
  };

  const linkByKey = {
    mentalhealthsymptoms: staff.linkTosymptoms,
    mentalhealthserviceneeds: staff.linkToservices,
    drugsalcohol: staff.linkTodrugsalcohol,
    vocational: staff.linkTovocational,
    educational: staff.linkToeducational,
    social: staff.linkTosocial,
    interpersonal: staff.linkTointerpersonal,
    selfcare: staff.linkToselfcare,
    medical: staff.linkTomedical,
    dental: staff.linkTodental,
    financial: staff.linkTofinancial,
    housing: staff.linkTohousing,
    transportation: staff.linkTotransportation,
    other: staff.linkToother,
  };

  const orderedRows = expectedRows.map((r) => ({
    label: r.label,
    status: pickText(statusByKey[r.key]) || "—",
    functioning: pickText(funcByKey[r.key], linkByKey[r.key], statusByKey[r.key]) || "—",
  }));

  // Plans 1..14
  const plans = Array.from({ length: 14 }, (_, i) => staff[`plan${i + 1}`]);

  // License holder checkbox
  const islicence = !!staff.islicence;

  // Names
  const staffName = pickText(staff.staffName, "—");
  // Per your instruction: use staffSignature for Clinical Supervisor
  const supervisorName = pickText(staff.staffSignature, "—");
  const directorName = pickText(staff.directorSignature, form.treatmentdirectorURL, "—");

  // Role selections
  const staffRoleKey = staff.version || ""; // "staffMHProfessional" etc.
  const supervisorRoleKey = staff.versionSuper || ""; // "supervisorMHPractitioner" etc.

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: "-16px",
          zIndex: (theme) => theme.zIndex.appBar,
          bgcolor: "#fff",
          p: 4,
          pb: 0,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" sx={{ position: "absolute", top: 16, left: 16 }}>
          <strong>DOS:</strong> {fmt(dos)}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            fontWeight: "bold",
          }}
        >
          {title}
        </Typography>

        <Box sx={{ height: 28 }} />
        <Divider />
      </Box>

      {/* Patient strip */}
      <Box sx={{ px: 4 }}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Typography variant="body2">
              <strong>File #</strong> {fileVal}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "center" }}>
            <Typography variant="body2">
              <strong>Client Name:</strong> {nameVal}
            </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "right" }}>
            <Typography variant="body2">
              <strong>DOB:</strong> {fmt(dobVal)}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={{ mb: 2 }} />
      </Box>

      {/* Six info lines */}
      <Box sx={{ mt: 1, px: 4 }}>
        <Typography sx={{ m: 0 }}><b>Recipient Name:</b> {nameVal}</Typography>
        <Typography sx={{ m: 0 }}><b>Client DOB:</b> {fmt(dobVal)}</Typography>
        <Typography sx={{ m: 0 }}><b>Client ID#:</b> {clientID || "—"}</Typography>
        <Typography sx={{ m: 0 }}><b>Time of Functional Assessment</b> {timeFA}</Typography>
        <Typography sx={{ m: 0 }}><b>Functional Assessment Date:</b> {fmt(faDate)}</Typography>
        <Typography sx={{ m: 0 }}><b>Diagnostic Assessment Date:</b> {fmt(diagnosticDate)}</Typography>
      </Box>

      {/* Field | Status | Functioning */}
      <Box sx={{ mt: 1, px: 4 }}>
        <TableContainer sx={{ width: "100%", border: "1px solid #ddd" }}>
          <Table size="small" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ border: "1px solid #ddd", fontWeight: 700, width: { md: "25%" } }}>
                  Field
                </TableCell>
                <TableCell sx={{ border: "1px solid #ddd", fontWeight: 700 }}>
                  Status
                </TableCell>
                <TableCell sx={{ border: "1px solid #ddd", fontWeight: 700 }}>
                  Functioning
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orderedRows.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell
                    sx={{
                      border: "1px solid #ddd",
                      verticalAlign: "top",
                      fontWeight: 700,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {row.label}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #ddd", verticalAlign: "top", whiteSpace: "pre-wrap" }}>
                    {row.status || "—"}
                  </TableCell>
                  <TableCell sx={{ border: "1px solid #ddd", verticalAlign: "top", whiteSpace: "pre-wrap" }}>
                    {row.functioning || "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Plan checkboxes 1..14 */}
      <Box sx={{ mt: 2, px: 4 }}>
        <Typography sx={{ m: 0 }}>Check which of the areas are addressed in the Treatment Plan:</Typography>
        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          {plans.map((checked, i) => (
            <Grid key={i} item xs="auto">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox checked={!!checked} disabled size="small" sx={{ p: 0, mr: 1 }} />
                <Typography sx={{ m: 0 }}>{i + 1}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Summary */}
      <Box sx={{ mt: 2, px: 4 }}>
        <Typography sx={{ m: 0 }}>
          Summarize the client’s strengths, resources, and all areas of functional impairment:
        </Typography>
        <TextareaAutosize
          minRows={3}
          value={staff.functionalImpairment || ""}
          readOnly
          style={{
            width: "100%",
            borderRadius: "8px",
            border: "2px solid #000",
            padding: "10px",
            resize: "none",
            marginTop: "6px",
          }}
        />
      </Box>

      {/* License holder checkbox */}
      <Box sx={{ mt: 1, px: 4, display: "flex", alignItems: "center" }}>
        <Checkbox checked={!!islicence} disabled size="small" sx={{ p: 0, mr: 1 }} />
        <Typography sx={{ m: 0 }}>
          License holder did not contact the client’s family and other natural supports
        </Typography>
      </Box>

      {/* Staff Name row */}
      <Box sx={{ mt: 2, px: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "12px",
              m: 0,
              width: LABEL_COL_WIDTH,
              flexShrink: 0,
            }}
          >
            Staff Name:
          </Typography>
          <TextField
            size="small"
            value={staffName}
            sx={{ flex: 1 }}
            InputProps={{ readOnly: true }}
          />
        </Box>

        {/* Roles under the input, aligned with field */}
        <Grid container spacing={1} sx={{ mt: 0.5, pl: `${LABEL_COL_WIDTH}px` }}>
          {staffRoles.map((r) => (
            <Grid key={r.key} item xs="auto">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  checked={staffRoleKey === r.key || !!staff[r.key]}
                  disabled
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                <Typography sx={{ m: 0 }}>{r.label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Clinical Supervisor row (uses staffSignature) */}
      <Box sx={{ mt: 2, px: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "12px",
              m: 0,
              width: LABEL_COL_WIDTH,
              flexShrink: 0,
            }}
          >
            Clinical Supervisor:
          </Typography>
          <TextField
            size="small"
            value={supervisorName}
            sx={{ flex: 1 }}
            InputProps={{ readOnly: true }}
          />
        </Box>

        {/* Roles under the input, aligned with field */}
        <Grid container spacing={1} sx={{ mt: 0.5, pl: `${LABEL_COL_WIDTH}px` }}>
          {supervisorRoles.map((r) => (
            <Grid key={r.key} item xs="auto">
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  checked={supervisorRoleKey === r.key || !!staff[r.key]}
                  disabled
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                />
                <Typography sx={{ m: 0 }}>{r.label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ARMHS Treatment Director Name row */}
      <Box sx={{ mt: 2, px: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "12px",
              m: 0,
              width: LABEL_COL_WIDTH,
              flexShrink: 0,
            }}
          >
            ARMHS Treatment Director Name:
          </Typography>
          <TextField
            size="small"
            value={directorName}
            sx={{ flex: 1 }}
            InputProps={{ readOnly: true }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ARMHSFunctionalAssessmentPreview;